import { useEffect, useRef, useState } from 'react';
import { Box, Button, Center, Text, VStack } from '@chakra-ui/react';
import { QR_SIGN_IN_LIMITS, type AuthResponse } from '@twinden/shared';
import { api, ApiError } from '../../lib/api';
import { guessDeviceKind } from '../../lib/devices';
import { useT } from '../../i18n';
import { TwindenLoader } from '../brand/TwindenLoader';
import { TwindenQr } from '../brand/TwindenQr';
import { useLoadingState } from '../../hooks/useLoadingState';

// The computer's half of "sign in by scanning" (M49, D157, D160): a QR the
// phone scans, then — once a phone has opened it — the number to pick on the
// phone (D159), and a quiet poll for the answer. A code lives 60 s; we swap
// it for a new one up to 10 times.
const CODE_LIFE_MS = QR_SIGN_IN_LIMITS.CODE_SECONDS * 1000;
const POLL_MS = QR_SIGN_IN_LIMITS.POLL_INTERVAL_MS;
const MAX_REPLACEMENTS = QR_SIGN_IN_LIMITS.MAX_REFRESHES;
// The code's card; the loader holds the same height so nothing jumps.
const CODE_SIZE = '224px';

type View = 'loading' | 'shown' | 'stopped' | 'failed' | 'limited';

export function ScanToSignIn({
  onApproved,
}: {
  onApproved: (
    user: AuthResponse['user'],
    tokens: AuthResponse['tokens']
  ) => void;
}) {
  const t = useT();
  const [view, setView] = useState<View>('loading');
  // The mark where the code will be, after 150 ms (M66, D248, D250).
  const codeWait = useLoadingState({ loading: view === 'loading' });
  // The link the code carries; drawn on this device with the mark (D261).
  const [url, setUrl] = useState<string | null>(null);
  // Only after a phone opened the code (D159): someone sent a screenshot of
  // the QR has no number on their screen to match.
  const [number, setNumber] = useState<number | null>(null);
  // Bumped by "Show a new code" to start a fresh run of the effect.
  const [run, setRun] = useState(0);
  const approvedRef = useRef(onApproved);
  approvedRef.current = onApproved;

  useEffect(() => {
    let alive = true;
    let replacements = 0;
    let pollSecret: string | null = null;
    let inFlight = false;
    // The code ran out while the tab was hidden: replace it once on return
    // rather than spending replacements nobody sees.
    let stale = false;
    // The code that ran out while hidden: the phone may have approved it
    // meanwhile, so it is asked once before being replaced (issue #23).
    let staleSecret: string | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;

    const clearTimers = () => {
      clearTimeout(pollTimer);
      clearTimeout(expiryTimer);
      pollTimer = expiryTimer = undefined;
    };

    const schedulePoll = (delay: number) => {
      clearTimeout(pollTimer);
      pollTimer = setTimeout(tick, delay);
    };

    const replace = () => {
      clearTimers();
      const old = pollSecret;
      pollSecret = null;
      setNumber(null);
      if (document.hidden) {
        stale = true;
        staleSecret = old ?? staleSecret;
        return;
      }
      if (replacements >= MAX_REPLACEMENTS) {
        setView('stopped');
        return;
      }
      replacements += 1;
      void newCode();
    };

    async function newCode() {
      try {
        const res = await api.createQrSignIn(guessDeviceKind());
        if (!alive) return;
        pollSecret = res.data.pollSecret;
        setUrl(res.data.url);
        setNumber(null);
        setView('shown');
        expiryTimer = setTimeout(replace, CODE_LIFE_MS);
        schedulePoll(POLL_MS);
      } catch (err) {
        if (!alive) return;
        setView(
          err instanceof ApiError && err.status === 429 ? 'limited' : 'failed'
        );
      }
    }

    async function tick() {
      pollTimer = undefined;
      if (!alive || !pollSecret || inFlight) return;
      // A hidden tab waits; visibilitychange brings us back.
      if (document.hidden) return;
      const secret = pollSecret;
      inFlight = true;
      try {
        const res = await api.pollQrSignIn(secret);
        if (!alive) return;
        const data = res.data;
        // An approval is never dropped, even for a code we just replaced:
        // the server already handed the session over (D160).
        if (data.status === 'approved') {
          alive = false;
          clearTimers();
          pollSecret = null;
          approvedRef.current(data.user, data.tokens);
        } else if (pollSecret === secret) {
          if (data.status === 'scanned') setNumber(data.number);
          else if (data.status !== 'pending') replace();
        }
      } catch {
        // Offline for a moment: the next tick tries again.
      } finally {
        inFlight = false;
        // Whichever code is current keeps being polled, even when it was
        // replaced while this poll was out.
        if (alive && pollSecret && pollTimer === undefined) {
          schedulePoll(POLL_MS);
        }
      }
    }

    // Back from hidden with a code that ran out: one last poll of it, since
    // an approval made while hidden must not be lost, then a new code.
    async function checkStaleThenReplace() {
      const secret = staleSecret;
      staleSecret = null;
      if (secret) {
        inFlight = true;
        try {
          const res = await api.pollQrSignIn(secret);
          if (!alive) return;
          if (res.data.status === 'approved') {
            alive = false;
            clearTimers();
            approvedRef.current(res.data.user, res.data.tokens);
            return;
          }
        } catch {
          // Offline: show a new code; the old one can't be told apart now.
        } finally {
          inFlight = false;
        }
      }
      if (alive) replace();
    }

    const onVisible = () => {
      if (document.hidden || !alive) return;
      if (stale) {
        stale = false;
        void checkStaleThenReplace();
      } else if (pollSecret && !inFlight) {
        schedulePoll(0);
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    void newCode();
    return () => {
      alive = false;
      clearTimers();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [run]);

  const again = () => {
    setView('loading');
    setRun((r) => r + 1);
  };

  const message =
    view === 'failed'
      ? 'account.scan.failed'
      : view === 'limited'
        ? 'account.scan.limited'
        : 'account.scan.stopped';

  return (
    <VStack
      spacing={3}
      align="center"
      bg="surface"
      borderRadius="24px"
      px={5}
      py={5}
    >
      <Text fontWeight={700} fontSize="17px" textAlign="center">
        {t('account.scan.heading')}
      </Text>
      {view === 'shown' && url ? (
        number === null ? (
          <>
            {/* Larger than the other codes: a phone camera reads this one
                off a monitor, and level H packs more modules in (D261). */}
            <TwindenQr
              value={url}
              label={t('account.scan.qrAlt')}
              size={CODE_SIZE}
            />
            <Text fontSize="14px" color="muted" textAlign="center">
              {t('account.scan.steps')}
            </Text>
          </>
        ) : (
          <Box textAlign="center" aria-live="polite">
            <Text
              fontSize="72px"
              lineHeight="1"
              fontWeight={800}
              color="accent"
              py={4}
            >
              {number}
            </Text>
            <Text fontSize="16px" fontWeight={600}>
              {t('account.scan.choose', { number })}
            </Text>
          </Box>
        )
      ) : view === 'loading' ? (
        // The mark where the code will be (M66, D250).
        <Center h={CODE_SIZE}>
          {codeWait.loader && <TwindenLoader size="inline" />}
        </Center>
      ) : (
        <>
          <Text fontSize="15px" color="ink.body" textAlign="center">
            {t(message)}
          </Text>
          <Button variant="outline" onClick={again}>
            {t('account.scan.newCode')}
          </Button>
        </>
      )}
    </VStack>
  );
}
